import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Flex,
  TextAreaField,
  Label,
  Text,
  Heading,
  Divider,
  TextField,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useFoodJournalPutMutation } from "../api/foodJournal/useFoodJournalMutation";
import { useFoodJournalQuery } from "../api/foodJournal/useFoodJournalQuery";
import { FaRegClock } from "react-icons/fa";

import { formatDate, formatTime } from "../../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";

const { z } = require("zod");

const putFoodJournalRequest = z.object({
  logDate: z.union([
    z.string({ message: "Log Date Required" }),
    z.date({ message: "Log Date Required" }),
  ]),
  hoursSlept: z
    .string({ message: "Hours Slept is Required" })
    .min(1, { message: "Hours Slept is required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  waterConsumption: z
    .string()
    .max(100, { message: "Must be 100 or fewer characters long" })
    .optional(),
  logWorkouts: z
    .string()
    .max(100, { message: "Must be 100 or fewer characters long" })
    .optional(),
  breakfastTime: z.union([z.string().nullish(), z.date().nullish()]).optional(),
  breakfastEatDrink: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  breakfastFeelAfter: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  lunchTime: z.union([z.string().nullish(), z.date().nullish()]).optional(),
  lunchEatDrink: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  lunchFeelAfter: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  dinnerTime: z.union([z.string().nullish(), z.date().nullish()]).optional(),
  dinnerEatDrink: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  dinnerFeelAfter: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  snacksTime: z.union([z.string().nullish(), z.date().nullish()]).optional(),
  snacksEatDrink: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
  snacksFeelAfter: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .optional(),
});

const defaultValues = {
  logDate: null,
  hoursSlept: "",
  waterConsumption: "",
  logWorkouts: "",
  breakfastTime: null,
  breakfastEatDrink: "",
  breakfastFeelAfter: "",
  lunchTime: null,
  lunchEatDrink: "",
  lunchFeelAfter: "",
  dinnerTime: null,
  dinnerEatDrink: "",
  dinnerFeelAfter: "",
  snacksTime: null,
  snacksEatDrink: "",
  snacksFeelAfter: "",
};

const setValidTime = (time) => {
  if (time === undefined || time === null) {
    return time;
  }
  if (typeof time === "string") {
    const date = new Date();
    const timeArray = time?.split(":");

    date.setHours(timeArray[0]);
    date.setMinutes(timeArray[1]);
    return date;
  }

  return new Date(time);
};

export default function MedicalConditionAddPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useAuthenticator((context) => [context.user]);

  const { data, isLoading, error } = useFoodJournalQuery(
    user.userId,
    params.id
  );

  const putFoodJournal = useFoodJournalPutMutation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    values: { ...data },
    resolver: zodResolver(putFoodJournalRequest),
  });

  useEffect(() => {
    if (data != null) {
      reset({
        ...data,
      });
    }
  }, [data]);

  if (isLoading) return "Loading...";

  if (error) return "Error loading Food Journal, check console for output";

  return (
    <>
      <form
        onSubmit={handleSubmit(async (values) => {
          try {
            const tmpDate = formatDate(new Date(values.logDate));
            let val = {
              id: params.id,
              logDate: tmpDate,
              hoursSlept: values.hoursSlept,
              waterConsumption: values.waterConsumption,
              logWorkouts: values.logWorkouts,
              breakfastTime: formatTime(setValidTime(values.breakfastTime)),
              breakfastEatDrink: values.breakfastEatDrink,
              breakfastFeelAfter: values.breakfastFeelAfter,
              lunchTime: formatTime(setValidTime(values.lunchTime)),
              lunchEatDrink: values.lunchEatDrink,
              lunchFeelAfter: values.lunchFeelAfter,
              dinnerTime: formatTime(setValidTime(values.dinnerTime)),
              dinnerEatDrink: values.dinnerEatDrink,
              dinnerFeelAfter: values.dinnerFeelAfter,
              snacksTime: formatTime(setValidTime(values.snacksTime)),
              snacksEatDrink: values.snacksEatDrink,
              snacksFeelAfter: values.snacksFeelAfter,
            };

            await putFoodJournal.mutateAsync(val);

            reset();
            navigate("/user/quick-actions/insights");
          } catch (error) {
            console.log("Error : ", error);
          }
        })}
      >
        <FlexFormContainer headerTitle={"Food Journal"}>
          <Flex
            direction={"row"}
            gap={12}
            alignItems="space-between"
            justifyContent="stretch"
          >
            <Flex direction={"column"} gap={12} style={{ flex: "1 0 25%" }}>
              <Label htmlFor="logDate" style={{ lineHeight: "18px" }}>
                Date <br />{" "}
                <Text as="span" fontSize="12px">
                  Log meals on this date
                </Text>{" "}
              </Label>
              <Controller
                name="logDate"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      {...field}
                      value={field.value != null ? new Date(field.value) : null}
                      format="yyyy-MM-dd"
                      oneTap
                      style={{ minHeight: "40px" }}
                    />
                    {!!errors.logDate && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.logDate.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Heading level={5} children={`Breakfast:`} />
              <Label htmlFor="logWorkouts" style={{ lineHeight: "18px" }}>
                Time: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log meals on this date
                </Text>{" "}
              </Label>
              <Controller
                name="breakfastTime"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={setValidTime(field.value) || null}
                      format="hh:mm aa"
                      caretAs={FaRegClock}
                      showMeridian
                    />
                    {!!errors.breakfastTime && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.breakfastTime.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Controller
                name="breakfastEatDrink"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="What did you eat/drink?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.breakfastEatDrink?.message}
                    hasError={!!errors.breakfastEatDrink}
                  />
                )}
              />
              <Controller
                name="breakfastFeelAfter"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="How did you feel after?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.breakfastFeelAfter?.message}
                    hasError={!!errors.breakfastFeelAfter}
                  />
                )}
              />
              <Heading level={5} children={`Dinner:`} />
              <Label htmlFor="logWorkouts" style={{ lineHeight: "18px" }}>
                Time: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log meals on this date
                </Text>{" "}
              </Label>
              <Controller
                name="dinnerTime"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={setValidTime(field.value) || null}
                      format="hh:mm aa"
                      caretAs={FaRegClock}
                      showMeridian
                    />
                    {!!errors.dinnerTime && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.dinnerTime.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Controller
                name="dinnerEatDrink"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="What did you eat/drink?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.dinnerEatDrink?.message}
                    hasError={!!errors.dinnerEatDrink}
                  />
                )}
              />
              <Controller
                name="dinnerFeelAfter"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="How did you feel after?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.dinnerFeelAfter?.message}
                    hasError={!!errors.dinnerFeelAfter}
                  />
                )}
              />
            </Flex>
            <Flex direction={"column"} gap={12} style={{ flex: "1 0 25%" }}>
              <Label htmlFor="hoursSlept" style={{ lineHeight: "18px" }}>
                Sleep <br />{" "}
                <Text as="span" fontSize="12px">
                  Hours slept
                </Text>{" "}
              </Label>
              <Controller
                name="hoursSlept"
                control={control}
                style={{ height: "80px" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    value={field.value}
                    gap={0.5}
                    style={{ marginBottom: "5px" }}
                    errorMessage={errors.hoursSlept?.message}
                    hasError={!!errors.hoursSlept}
                  />
                )}
              />
              <Heading level={5} children={`Lunch:`} />
              <Label htmlFor="logWorkouts" style={{ lineHeight: "18px" }}>
                Time: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log meals on this date
                </Text>{" "}
              </Label>
              <Controller
                name="lunchTime"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={setValidTime(field.value) || null}
                      format="hh:mm aa"
                      caretAs={FaRegClock}
                      showMeridian
                    />
                    {!!errors.lunchTime && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.lunchTime.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Controller
                name="lunchEatDrink"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="What did you eat/drink?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.lunchEatDrink?.message}
                    hasError={!!errors.lunchEatDrink}
                  />
                )}
              />
              <Controller
                name="lunchFeelAfter"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="How did you feel after?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.lunchFeelAfter?.message}
                    hasError={!!errors.lunchFeelAfter}
                  />
                )}
              />
              <Heading level={5} children={`Snacks:`} />
              <Label htmlFor="logWorkouts" style={{ lineHeight: "18px" }}>
                Time: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log meals on this date
                </Text>{" "}
              </Label>
              <Controller
                name="snacksTime"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={setValidTime(field.value) || null}
                      format="hh:mm aa"
                      caretAs={FaRegClock}
                      showMeridian
                    />
                    {!!errors.snacksTime && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.snacksTime.message}
                      </Text>
                    )}
                  </>
                )}
              />
              <Controller
                name="snacksEatDrink"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="What did you eat/drink?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.snacksEatDrink?.message}
                    hasError={!!errors.snacksEatDrink}
                  />
                )}
              />
              <Controller
                name="snacksFeelAfter"
                control={control}
                render={({ field }) => (
                  <TextAreaField
                    minWidth="300px"
                    label="How did you feel after?"
                    {...field}
                    value={field.value}
                    errorMessage={errors.snacksFeelAfter?.message}
                    hasError={!!errors.snacksFeelAfter}
                  />
                )}
              />
            </Flex>
          </Flex>
          <Divider height="1px" size="small" orientation="horizontal"></Divider>
          <Flex direction={"row"} gap={12} width="100%">
            <Flex direction={"column"} gap={4}>
              <Label htmlFor="waterConsumption" style={{ lineHeight: "18px" }}>
                Water: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log water consumption
                </Text>{" "}
              </Label>
              <Controller
                name="waterConsumption"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value}
                    style={{ minWidth: "300px" }}
                    errorMessage={errors.waterConsumption?.message}
                    hasError={!!errors.waterConsumption}
                  />
                )}
              />
            </Flex>
            <Flex direction={"column"} gap={4}>
              <Label htmlFor="logWorkouts" style={{ lineHeight: "18px" }}>
                Workout: <br />{" "}
                <Text as="span" fontSize="12px">
                  Log workouts
                </Text>{" "}
              </Label>
              <Controller
                name="logWorkouts"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value}
                    style={{ minWidth: "300px" }}
                    errorMessage={errors.logWorkouts?.message}
                    hasError={!!errors.logWorkouts}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex gap="12" direction="column">
            <Button variation="primary" type="submit">
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
